<template>
  <div class="pc__swatch-container">
    <div
      :class="[
        'pc__swatches',
        {
          'pc__swatches--2026': newProductCard,
          'pc__swatches--has-left-fade': newProductCard && leftScroll > 1,
          'pc__swatches--has-right-fade': newProductCard && leftScroll < rightScroll - 1,
        },
      ]"
    >
      <div
        v-if="!newProductCard && !disableScrollIcon"
        :class="['pc__swatches-prev', { 'pc__swatches--blur': leftScroll <= 1 }]"
        @click="scrollSwatches('left')"
        role="button"
        :aria-label="leftScroll <= 1 ? 'No more items to scroll left' : 'Scroll left through color options'"
        :aria-disabled="leftScroll <= 1 ? true : false"
        @keydown="(e) => e.key === 'Enter' && scrollSwatches('left')"
      >
        <svg class="pc__swatches-prev-icon">
          <use xlink:href="#icon-prev"></use>
        </svg>
      </div>
      <div
        ref="swatchWrapper"
        :class="[
          'pc__swatches-wrapper',
          {
            'pc__swatches-wrapper--2026': newProductCard,
            'swiper-no-swiping': newProductCard,
          },
        ]"
        @scroll="updateScrollStates"
        @touchstart.stop
        @touchmove.stop
        @pointerdown.stop
        @pointermove.stop
      >
        <div
          v-for="(value, name) in colorOptions"
          tabindex="0"
          :key="name"
          :class="['pc__swatchButton', { active: name === selectedColor }]"
          @click="(e) => switchActiveSwatch(e, name)"
          @keydown="(e) => handleKeyDownOnSwatches(e, name)"
          role="button"
          :aria-label="'Select color ' + name"
          :aria-pressed="name === selectedColor"
        >
          <ImageField
            v-if="value.image"
            class="pc__swatchImage"
            :obj="{ url: value.image.src, alt: value.image.alt, width: 32, height: 32 }"
            :isLoadingLazy="isLoadingLazy"
          />
        </div>
      </div>
      <div
        v-if="!newProductCard && !disableScrollIcon"
        :class="['pc__swatches-next', { 'pc__swatches--blur': leftScroll >= rightScroll - 1 }]"
        @click="scrollSwatches('right')"
        role="button"
        :aria-label="leftScroll >= rightScroll - 1 ? 'No more items to scroll right' : 'Scroll right through color options'"
        :aria-disabled="leftScroll >= rightScroll - 1 ? true : false"
        @keydown="(e) => e.key === 'Enter' && scrollSwatches('right')"
      >
        <svg class="pc__swatches-next-icon">
          <use xlink:href="#icon-next"></use>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, toRefs, onMounted, onBeforeUnmount, nextTick } from 'vue';
  import ImageField from '../image.vue';

  const props = defineProps({
    colorOptions: { type: Object, required: true },
    selectedColor: { type: String, required: true },
    isLoadingLazy: { type: Boolean, default: false },
    newProductCard: { type: Boolean, default: false },
    windowWidth: { type: Number, required: true },
  });

  const { colorOptions, selectedColor, isLoadingLazy, newProductCard, windowWidth } = toRefs(props);

  const emit = defineEmits(['color-change']);

  const swatchWrapper = ref(null);
  const leftScroll = ref(0);
  const rightScroll = ref(0);
  let resizeObserver = null;

  // Keep arrow/fade visibility tied to reactive scroll metrics (not a one-shot DOM read).
  const disableScrollIcon = computed(() => {
    if (newProductCard.value) return true;
    return rightScroll.value <= 1;
  });

  const updateScrollStates = () => {
    const el = swatchWrapper.value;
    if (!el) return;

    leftScroll.value = el.scrollLeft;
    rightScroll.value = Math.max(0, el.scrollWidth - el.offsetWidth);
  };

  const scheduleScrollUpdate = () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        updateScrollStates();
      });
    });
  };

  const observeSwatchWrapper = () => {
    if (!swatchWrapper.value || typeof ResizeObserver === 'undefined') return;

    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(() => {
      updateScrollStates();
    });
    resizeObserver.observe(swatchWrapper.value);
  };

  onMounted(() => {
    observeSwatchWrapper();
    scheduleScrollUpdate();
    // Re-measure after carousels (e.g. CTL) finish setting slide widths.
    setTimeout(updateScrollStates, 250);
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
  });

  watch(colorOptions, () => {
    scheduleScrollUpdate();
  });

  watch(windowWidth, () => {
    scheduleScrollUpdate();
  });

  const switchActiveSwatch = (e, name) => {
    e.preventDefault();
    emit('color-change', name);
  };

  const scrollSwatches = (pos) => {
    const element = swatchWrapper.value;
    if (!element) return;

    if (pos === 'left') {
      element.scrollLeft -= 100;
    } else {
      element.scrollLeft += 100;
    }
    updateScrollStates();
  };

  const handleKeyDownOnSwatches = (event, name) => {
    if (event.key === 'Enter' || event.key === ' ') {
      switchActiveSwatch(event, name);
    }
  };
</script>
